// Montageservice — adds an extra "Montageservice" line item to the cart when toggle is on.
// Strategy: leave the theme's product-form flow intact (it adds the main product + our hidden
// property input), then on a successful CartAddEvent we add the Montageservice variant in a
// separate /cart/add.js call.
import { CartAddEvent, ThemeEvents } from "@theme/events";

class MontageServiceComponent extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.form = this.findProductForm();

    console.log("[montage-service] init", {
      checkbox: !!this.checkbox,
      form: !!this.form,
      assemblyVariantId: this.dataset.assemblyVariantId,
    });

    if (!this.checkbox) return;

    this._onChange = this.onChange.bind(this);
    this.checkbox.addEventListener("change", this._onChange);

    this.syncCheckedClass();
    this.syncFormProperty();

    this._onCartAdd = this.onCartAdd.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartAdd);
  }

  disconnectedCallback() {
    if (this.checkbox && this._onChange) {
      this.checkbox.removeEventListener("change", this._onChange);
    }
    if (this._onCartAdd) {
      document.removeEventListener(ThemeEvents.cartUpdate, this._onCartAdd);
    }
  }

  findProductForm() {
    const ancestor = this.closest('form[data-type="add-to-cart-form"]');
    if (ancestor) return ancestor;
    const sectionRoot = this.closest("product-information-component, [data-section-id]");
    if (sectionRoot) {
      const formInSection = sectionRoot.querySelector('form[data-type="add-to-cart-form"]');
      if (formInSection) return formInSection;
    }
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  onChange() {
    this.syncCheckedClass();
    this.syncFormProperty();
  }

  syncCheckedClass() {
    if (!this.checkbox) return;
    this.classList.toggle("is-checked", this.checkbox.checked);
  }

  generatePairId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  ensureHiddenInput(name, value, dataKey) {
    const existing = this.form.querySelector(`input[${dataKey}="true"]`);
    if (existing) {
      existing.name = name;
      existing.value = value;
      return existing;
    }
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.setAttribute(dataKey, "true");
    this.form.appendChild(input);
    return input;
  }

  removeHiddenInput(dataKey) {
    const existing = this.form && this.form.querySelector(`input[${dataKey}="true"]`);
    if (existing) existing.remove();
  }

  // Add or remove a single hidden coupling property on the product form so it
  // travels along with the normal /cart/add submit:
  //   properties[_montage_pair_id] = <pairId>   (underscore-prefix hides it in the cart UI)
  // The separate "Montageservice" line item already shows the service + price, so we
  // intentionally do NOT add a visible "Inclusief montage: Ja" property (confusing duplicate).
  syncFormProperty() {
    if (!this.form || !this.checkbox) return;

    if (this.checkbox.checked) {
      if (!this._pairId) this._pairId = this.generatePairId();
      this.ensureHiddenInput("properties[_montage_pair_id]", this._pairId, "data-montage-pair-id");
    } else {
      this.removeHiddenInput("data-montage-pair-id");
      this._pairId = null;
    }
  }

  async onCartAdd(event) {
    if (!this.checkbox || !this.checkbox.checked) return;

    const source = event.detail && event.detail.data && event.detail.data.source;

    // Ignore our own dispatched events to avoid loops.
    if (source === "montage-service") return;

    // Only react to a successful add originating from the product form on this page.
    if (source !== "product-form-component") return;

    // Guard against double-firing for the same add.
    if (this._adding) return;
    this._adding = true;

    const assemblyVariantId = this.dataset.assemblyVariantId;
    if (!assemblyVariantId) {
      console.warn("[montage-service] no assemblyVariantId — skipping extra add");
      this._adding = false;
      return;
    }

    try {
      const cartAddUrl =
        (window.MorgennTheme && window.MorgennTheme.routes && window.MorgennTheme.routes.cart_add_url) || "/cart/add.js";
      const cartUrl = (window.MorgennTheme && window.MorgennTheme.routes && window.MorgennTheme.routes.cart) || "/cart.js";

      const sectionIds = new Set();
      document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
        if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
      });

      const fd = new FormData();
      fd.append("id", assemblyVariantId);
      fd.append("quantity", "1");
      if (sectionIds.size > 0) {
        fd.append("sections", Array.from(sectionIds).join(","));
      }

      // Link this montage line item to its parent product via a shared pair-id.
      // The parent product was just submitted with the same _montage_pair_id property.
      const pairInput = this.form && this.form.querySelector('input[data-montage-pair-id="true"]');
      const pairId = this._pairId || (pairInput && pairInput.value);
      if (pairId) {
        fd.append("properties[_montage_pair_id]", pairId);
        fd.append("properties[_is_montage_addon]", "1");
      }

      const addRes = await fetch(cartAddUrl, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });

      if (!addRes.ok) {
        throw new Error("cart/add failed: " + addRes.status);
      }

      const addData = await addRes.json();

      const cartRes = await fetch(cartUrl);
      const cart = cartRes.ok ? await cartRes.json() : {};

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || "montage-service", {
          itemCount: cart.item_count,
          source: "montage-service",
          productId: assemblyVariantId,
          variantId: assemblyVariantId,
          sections: addData.sections,
        })
      );

      // Reset the toggle so the next product the customer adds doesn't auto-include montage.
      this.checkbox.checked = false;
      this.syncCheckedClass();
      this.syncFormProperty();
    } catch (err) {
      console.error("[montage-service]", err);
    } finally {
      this._adding = false;
    }
  }
}

if (!customElements.get("montage-service-component")) {
  customElements.define("montage-service-component", MontageServiceComponent);
}
